import { normalizeLocale, type SupportedLocale } from './config';

/** Home folders that get a one-line "what is this for" hint in My Drive. */
export const MY_DRIVE_HINTED_FOLDER_NAMES = [
  'Documents',
  'Chats',
  'Avatars',
  'Products',
  'Projects',
  'Apps',
  'Blueprints',
  'References',
  'skills',
  'Uploads',
] as const;

export type MyDriveHintedFolderName =
  (typeof MY_DRIVE_HINTED_FOLDER_NAMES)[number];

export type MyDriveWayfindingCopy = {
  recentTitle: string;
  recentDescription: string;
  recentEmpty: string;
  recentViewAll: string;
  recentFromTask: (title: string) => string;
  untitledTask: string;
  /** The folder header's link back to the task the folder belongs to. */
  openTask: string;
  /** Accessible name and tooltip of a link that opens a named task. */
  openTaskNamed: (title: string) => string;
  filesFromThisTask: string;
  brandsHint: string;
  folderHints: Record<MyDriveHintedFolderName, string>;
};

const COPY = {
  en: {
    recentTitle: 'Recent',
    recentDescription: 'Files you made or uploaded, newest first',
    recentEmpty:
      'Nothing here yet. Files you generate in a task or upload show up here.',
    recentViewAll: 'View all',
    recentFromTask: (title) => `From ${title}`,
    untitledTask: 'Untitled task',
    openTask: 'Open task',
    openTaskNamed: (title) => `Open ${title}`,
    filesFromThisTask: 'Files from this task',
    brandsHint: 'Brand context the agent uses',
    folderHints: {
      Documents: 'Your notes and documents',
      Chats: 'Files from your tasks',
      Avatars: 'Avatars you created',
      Products: 'Your product files',
      Projects: 'Project folders',
      Apps: 'Apps you built',
      Blueprints: 'Your tech packs',
      References: 'Files the agent can reference',
      skills: 'Custom skills for the agent',
      Uploads: 'Files you uploaded',
    },
  },
  'pt-BR': {
    recentTitle: 'Recentes',
    recentDescription:
      'Arquivos que você criou ou enviou, os mais novos primeiro',
    recentEmpty:
      'Nada ainda. Os arquivos que você gerar em uma tarefa ou enviar aparecem aqui.',
    recentViewAll: 'Ver tudo',
    recentFromTask: (title) => `De ${title}`,
    untitledTask: 'Tarefa sem título',
    openTask: 'Abrir tarefa',
    openTaskNamed: (title) => `Abrir ${title}`,
    filesFromThisTask: 'Arquivos desta tarefa',
    brandsHint: 'Contexto de marca usado pelo agente',
    folderHints: {
      Documents: 'Suas notas e documentos',
      Chats: 'Arquivos das suas tarefas',
      Avatars: 'Avatares que você criou',
      Products: 'Arquivos dos seus produtos',
      Projects: 'Pastas de projetos',
      Apps: 'Apps que você criou',
      Blueprints: 'Suas fichas técnicas',
      References: 'Arquivos que o agente pode consultar',
      skills: 'Habilidades personalizadas do agente',
      Uploads: 'Arquivos que você enviou',
    },
  },
  ar: {
    recentTitle: 'الأحدث',
    recentDescription: 'الملفات التي أنشأتها أو رفعتها، الأحدث أولًا',
    recentEmpty: 'لا شيء بعد. ستظهر هنا الملفات التي تنشئها في مهمة أو ترفعها.',
    recentViewAll: 'عرض الكل',
    recentFromTask: (title) => `من ${title}`,
    untitledTask: 'مهمة بلا عنوان',
    openTask: 'فتح المهمة',
    openTaskNamed: (title) => `فتح ${title}`,
    filesFromThisTask: 'ملفات هذه المهمة',
    brandsHint: 'سياق العلامة التجارية الذي يستخدمه الوكيل',
    folderHints: {
      Documents: 'ملاحظاتك ومستنداتك',
      Chats: 'ملفات من مهامك',
      Avatars: 'الصور الرمزية التي أنشأتها',
      Products: 'ملفات منتجاتك',
      Projects: 'مجلدات المشاريع',
      Apps: 'التطبيقات التي أنشأتها',
      Blueprints: 'حزمك التقنية',
      References: 'ملفات يمكن للوكيل الرجوع إليها',
      skills: 'مهارات مخصصة للوكيل',
      Uploads: 'الملفات التي رفعتها',
    },
  },
  es: {
    recentTitle: 'Recientes',
    recentDescription: 'Archivos que creaste o subiste, los más nuevos primero',
    recentEmpty:
      'Aún no hay nada. Aquí aparecerán los archivos que generes en una tarea o subas.',
    recentViewAll: 'Ver todo',
    recentFromTask: (title) => `De ${title}`,
    untitledTask: 'Tarea sin título',
    openTask: 'Abrir tarea',
    openTaskNamed: (title) => `Abrir ${title}`,
    filesFromThisTask: 'Archivos de esta tarea',
    brandsHint: 'Contexto de marca que usa el agente',
    folderHints: {
      Documents: 'Tus notas y documentos',
      Chats: 'Archivos de tus tareas',
      Avatars: 'Avatares que creaste',
      Products: 'Archivos de tus productos',
      Projects: 'Carpetas de proyectos',
      Apps: 'Apps que creaste',
      Blueprints: 'Tus fichas técnicas',
      References: 'Archivos que el agente puede consultar',
      skills: 'Habilidades personalizadas del agente',
      Uploads: 'Archivos que subiste',
    },
  },
  fr: {
    recentTitle: 'Récents',
    recentDescription:
      'Fichiers que vous avez créés ou importés, du plus récent au plus ancien',
    recentEmpty:
      'Rien pour l’instant. Les fichiers générés dans une tâche ou importés apparaîtront ici.',
    recentViewAll: 'Tout afficher',
    recentFromTask: (title) => `De ${title}`,
    untitledTask: 'Tâche sans titre',
    openTask: 'Ouvrir la tâche',
    openTaskNamed: (title) => `Ouvrir ${title}`,
    filesFromThisTask: 'Fichiers de cette tâche',
    brandsHint: 'Contexte de marque utilisé par l’agent',
    folderHints: {
      Documents: 'Vos notes et documents',
      Chats: 'Fichiers de vos tâches',
      Avatars: 'Avatars que vous avez créés',
      Products: 'Fichiers de vos produits',
      Projects: 'Dossiers de projets',
      Apps: 'Apps que vous avez créées',
      Blueprints: 'Vos fiches techniques',
      References: 'Fichiers que l’agent peut consulter',
      skills: 'Compétences personnalisées de l’agent',
      Uploads: 'Fichiers que vous avez importés',
    },
  },
  it: {
    recentTitle: 'Recenti',
    recentDescription: 'File che hai creato o caricato, dal più recente',
    recentEmpty:
      'Ancora niente. Qui compariranno i file che generi in un’attività o carichi.',
    recentViewAll: 'Mostra tutto',
    recentFromTask: (title) => `Da ${title}`,
    untitledTask: 'Attività senza titolo',
    openTask: 'Apri attività',
    openTaskNamed: (title) => `Apri ${title}`,
    filesFromThisTask: 'File di questa attività',
    brandsHint: 'Contesto del brand usato dall’agente',
    folderHints: {
      Documents: 'Le tue note e i tuoi documenti',
      Chats: 'File delle tue attività',
      Avatars: 'Avatar che hai creato',
      Products: 'File dei tuoi prodotti',
      Projects: 'Cartelle dei progetti',
      Apps: 'App che hai creato',
      Blueprints: 'Le tue schede tecniche',
      References: 'File che l’agente può consultare',
      skills: 'Skill personalizzate dell’agente',
      Uploads: 'File che hai caricato',
    },
  },
  ko: {
    recentTitle: '최근 항목',
    recentDescription: '내가 만들거나 업로드한 파일, 최신순',
    recentEmpty:
      '아직 없습니다. 작업에서 생성하거나 업로드한 파일이 여기에 표시됩니다.',
    recentViewAll: '모두 보기',
    recentFromTask: (title) => `${title}에서`,
    untitledTask: '제목 없는 작업',
    openTask: '작업 열기',
    openTaskNamed: (title) => `${title} 열기`,
    filesFromThisTask: '이 작업의 파일',
    brandsHint: '에이전트가 사용하는 브랜드 컨텍스트',
    folderHints: {
      Documents: '내 메모와 문서',
      Chats: '내 작업의 파일',
      Avatars: '내가 만든 아바타',
      Products: '내 제품 파일',
      Projects: '프로젝트 폴더',
      Apps: '내가 만든 앱',
      Blueprints: '내 테크팩',
      References: '에이전트가 참고할 수 있는 파일',
      skills: '에이전트용 맞춤 스킬',
      Uploads: '내가 업로드한 파일',
    },
  },
  de: {
    recentTitle: 'Neueste',
    recentDescription:
      'Dateien, die du erstellt oder hochgeladen hast, neueste zuerst',
    recentEmpty:
      'Noch nichts da. Dateien, die du in einer Aufgabe erzeugst oder hochlädst, erscheinen hier.',
    recentViewAll: 'Alle anzeigen',
    recentFromTask: (title) => `Aus ${title}`,
    untitledTask: 'Unbenannte Aufgabe',
    openTask: 'Aufgabe öffnen',
    openTaskNamed: (title) => `${title} öffnen`,
    filesFromThisTask: 'Dateien aus dieser Aufgabe',
    brandsHint: 'Markenkontext, den der Agent nutzt',
    folderHints: {
      Documents: 'Deine Notizen und Dokumente',
      Chats: 'Dateien aus deinen Aufgaben',
      Avatars: 'Von dir erstellte Avatare',
      Products: 'Deine Produktdateien',
      Projects: 'Projektordner',
      Apps: 'Von dir erstellte Apps',
      Blueprints: 'Deine Tech Packs',
      References: 'Dateien, die der Agent nutzen kann',
      skills: 'Eigene Skills für den Agenten',
      Uploads: 'Von dir hochgeladene Dateien',
    },
  },
  ja: {
    recentTitle: '最近',
    recentDescription: '作成またはアップロードしたファイル(新しい順)',
    recentEmpty:
      'まだありません。タスクで生成したりアップロードしたりしたファイルがここに表示されます。',
    recentViewAll: 'すべて表示',
    recentFromTask: (title) => `${title} から`,
    untitledTask: '無題のタスク',
    openTask: 'タスクを開く',
    openTaskNamed: (title) => `${title} を開く`,
    filesFromThisTask: 'このタスクのファイル',
    brandsHint: 'エージェントが使うブランド情報',
    folderHints: {
      Documents: 'メモとドキュメント',
      Chats: 'タスクで作られたファイル',
      Avatars: '作成したアバター',
      Products: '製品ファイル',
      Projects: 'プロジェクトフォルダー',
      Apps: '作成したアプリ',
      Blueprints: 'テックパック',
      References: 'エージェントが参照できるファイル',
      skills: 'エージェント用のカスタムスキル',
      Uploads: 'アップロードしたファイル',
    },
  },
  hi: {
    recentTitle: 'हाल के',
    recentDescription: 'आपकी बनाई या अपलोड की गई फ़ाइलें, नई पहले',
    recentEmpty:
      'अभी कुछ नहीं है। किसी टास्क में बनाई या अपलोड की गई फ़ाइलें यहाँ दिखेंगी।',
    recentViewAll: 'सभी देखें',
    recentFromTask: (title) => `${title} से`,
    untitledTask: 'बिना शीर्षक का टास्क',
    openTask: 'टास्क खोलें',
    openTaskNamed: (title) => `${title} खोलें`,
    filesFromThisTask: 'इस टास्क की फ़ाइलें',
    brandsHint: 'एजेंट द्वारा उपयोग किया जाने वाला ब्रांड संदर्भ',
    folderHints: {
      Documents: 'आपके नोट्स और दस्तावेज़',
      Chats: 'आपके टास्क की फ़ाइलें',
      Avatars: 'आपके बनाए अवतार',
      Products: 'आपकी प्रोडक्ट फ़ाइलें',
      Projects: 'प्रोजेक्ट फ़ोल्डर',
      Apps: 'आपके बनाए ऐप',
      Blueprints: 'आपके टेक पैक',
      References: 'फ़ाइलें जिन्हें एजेंट देख सकता है',
      skills: 'एजेंट के लिए कस्टम स्किल',
      Uploads: 'आपकी अपलोड की गई फ़ाइलें',
    },
  },
  ru: {
    recentTitle: 'Недавние',
    recentDescription: 'Файлы, которые вы создали или загрузили, сначала новые',
    recentEmpty:
      'Пока пусто. Здесь появятся файлы, созданные в задачах или загруженные вами.',
    recentViewAll: 'Показать все',
    recentFromTask: (title) => `Из «${title}»`,
    untitledTask: 'Задача без названия',
    openTask: 'Открыть задачу',
    openTaskNamed: (title) => `Открыть «${title}»`,
    filesFromThisTask: 'Файлы этой задачи',
    brandsHint: 'Контекст бренда, который использует агент',
    folderHints: {
      Documents: 'Ваши заметки и документы',
      Chats: 'Файлы из ваших задач',
      Avatars: 'Созданные вами аватары',
      Products: 'Файлы ваших продуктов',
      Projects: 'Папки проектов',
      Apps: 'Созданные вами приложения',
      Blueprints: 'Ваши техпаки',
      References: 'Файлы, к которым обращается агент',
      skills: 'Пользовательские навыки агента',
      Uploads: 'Загруженные вами файлы',
    },
  },
  zh: {
    recentTitle: '最近',
    recentDescription: '你创建或上传的文件,最新的在前',
    recentEmpty: '暂无内容。你在任务中生成或上传的文件会显示在这里。',
    recentViewAll: '查看全部',
    recentFromTask: (title) => `来自 ${title}`,
    untitledTask: '未命名任务',
    openTask: '打开任务',
    openTaskNamed: (title) => `打开 ${title}`,
    filesFromThisTask: '此任务的文件',
    brandsHint: '智能体使用的品牌信息',
    folderHints: {
      Documents: '你的笔记和文档',
      Chats: '来自你任务的文件',
      Avatars: '你创建的头像',
      Products: '你的产品文件',
      Projects: '项目文件夹',
      Apps: '你构建的应用',
      Blueprints: '你的技术包',
      References: '智能体可参考的文件',
      skills: '智能体的自定义技能',
      Uploads: '你上传的文件',
    },
  },
  'zh-TW': {
    recentTitle: '最近',
    recentDescription: '你建立或上傳的檔案,最新的在前',
    recentEmpty: '目前沒有內容。你在任務中產生或上傳的檔案會顯示在這裡。',
    recentViewAll: '查看全部',
    recentFromTask: (title) => `來自 ${title}`,
    untitledTask: '未命名任務',
    openTask: '開啟任務',
    openTaskNamed: (title) => `開啟 ${title}`,
    filesFromThisTask: '此任務的檔案',
    brandsHint: '代理使用的品牌資訊',
    folderHints: {
      Documents: '你的筆記與文件',
      Chats: '來自你任務的檔案',
      Avatars: '你建立的虛擬形象',
      Products: '你的產品檔案',
      Projects: '專案資料夾',
      Apps: '你建置的應用程式',
      Blueprints: '你的技術包',
      References: '代理可參考的檔案',
      skills: '代理的自訂技能',
      Uploads: '你上傳的檔案',
    },
  },
  vi: {
    recentTitle: 'Gần đây',
    recentDescription: 'Tệp bạn đã tạo hoặc tải lên, mới nhất trước',
    recentEmpty:
      'Chưa có gì. Tệp bạn tạo trong một tác vụ hoặc tải lên sẽ xuất hiện ở đây.',
    recentViewAll: 'Xem tất cả',
    recentFromTask: (title) => `Từ ${title}`,
    untitledTask: 'Tác vụ chưa đặt tên',
    openTask: 'Mở tác vụ',
    openTaskNamed: (title) => `Mở ${title}`,
    filesFromThisTask: 'Tệp của tác vụ này',
    brandsHint: 'Ngữ cảnh thương hiệu mà tác tử sử dụng',
    folderHints: {
      Documents: 'Ghi chú và tài liệu của bạn',
      Chats: 'Tệp từ các tác vụ của bạn',
      Avatars: 'Avatar bạn đã tạo',
      Products: 'Tệp sản phẩm của bạn',
      Projects: 'Thư mục dự án',
      Apps: 'Ứng dụng bạn đã tạo',
      Blueprints: 'Tech pack của bạn',
      References: 'Tệp tác tử có thể tham khảo',
      skills: 'Kỹ năng tùy chỉnh cho tác tử',
      Uploads: 'Tệp bạn đã tải lên',
    },
  },
  id: {
    recentTitle: 'Terbaru',
    recentDescription: 'File yang Anda buat atau unggah, terbaru lebih dulu',
    recentEmpty:
      'Belum ada apa pun. File yang Anda buat dalam tugas atau unggah akan muncul di sini.',
    recentViewAll: 'Lihat semua',
    recentFromTask: (title) => `Dari ${title}`,
    untitledTask: 'Tugas tanpa judul',
    openTask: 'Buka tugas',
    openTaskNamed: (title) => `Buka ${title}`,
    filesFromThisTask: 'File dari tugas ini',
    brandsHint: 'Konteks merek yang digunakan agen',
    folderHints: {
      Documents: 'Catatan dan dokumen Anda',
      Chats: 'File dari tugas Anda',
      Avatars: 'Avatar yang Anda buat',
      Products: 'File produk Anda',
      Projects: 'Folder proyek',
      Apps: 'Aplikasi yang Anda buat',
      Blueprints: 'Tech pack Anda',
      References: 'File yang bisa dirujuk agen',
      skills: 'Skill khusus untuk agen',
      Uploads: 'File yang Anda unggah',
    },
  },
} satisfies Record<SupportedLocale, MyDriveWayfindingCopy>;

export function getMyDriveWayfindingCopy(
  locale: string
): MyDriveWayfindingCopy {
  return COPY[normalizeLocale(locale)];
}
